'use client'

import { ReactNode } from 'react'
import { useTranslations } from 'next-intl'
import { AlertCircle } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'

interface LoadingStateProps {
  isLoading: boolean
  error?: string | null
  onRetry?: () => void
  loadingRows?: number
  children: ReactNode
}

const LoadingState = ({ isLoading, error, onRetry, loadingRows = 5, children }: LoadingStateProps) => {
  const t = useTranslations()

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: loadingRows }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-3">
        {/* Error state with skeleton-like design */}
        <div className="border-muted-foreground/25 bg-muted/10 rounded-lg border border-dashed p-4">
          <div className="flex items-center justify-between">
            <div className="text-muted-foreground flex items-center gap-2 text-sm">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
            {onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                {t('try_again')}
              </Button>
            )}
          </div>
        </div>
        {/* Show some skeleton rows to maintain layout */}
        {Array.from({ length: Math.min(loadingRows, 3) }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full opacity-30" />
        ))}
      </div>
    )
  }

  return <>{children}</>
}

export default LoadingState
